import { SectionHeader } from '@mdvip/shared/components/blocks/SectionHeader';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { Can } from '@mdvip/shared/system/AbilityProvider';
import { useTranslation } from 'react-i18next';
import { AwpDocumentsButton } from '@/components/awp/AwpDocumentsButton';
import { useAwpStatus } from '@/hooks/member/useAwpStatus';
import { AwpStatusDates } from './AwpStatusDates';

/**
 * The dashboard's "Health Themes" header. The title and description are static, so they render at
 * once. The aside leads with the "Download AWP" button for members who can read their AWP documents;
 * it opens a modal that loads its own data, so it never waits on `myAwpStatus`. Only the dates do:
 * while they load they stand as skeletons, and on any failure they are simply withheld, since they
 * are context, not content, with no toast or inline alert to dismiss.
 *
 * The `@container/health-themes` wrapper is what the dates row queries to decide whether the `|`
 * divider fits.
 */
export function HealthThemesHeader() {
  const { t } = useTranslation('members');
  const { data, isPending, isError } = useAwpStatus();

  let dates = null;
  if (isPending) {
    dates = (
      <div className="flex flex-wrap items-center gap-4" aria-busy="true">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-36" />
      </div>
    );
  } else if (!isError && data) {
    dates = <AwpStatusDates status={data} />;
  }

  return (
    <div className="@container/health-themes">
      <SectionHeader
        as="h2"
        size="h4"
        asideOn="title"
        title={t('pages.dashboard.healthThemes.title')}
        description={t('pages.dashboard.healthThemes.description')}
        aside={
          <div className="flex flex-wrap items-center gap-4">
            <Can I="read" a="Awp">
              <AwpDocumentsButton />
            </Can>
            {dates}
          </div>
        }
      />
    </div>
  );
}
